import React, { useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import API from '@utils/api';
import { Container, Button, CheckboxInput } from '@components';
import styles from './EmailForm.module.scss';

const phoneRegExp = /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/;
const emailRegExp =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
const postalRegExp = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;

const formSchema = Yup.object().shape({
  firstname: Yup.string().required('Please enter your first name'),
  lastname: Yup.string().required('Please enter your last name'),
  phone: Yup.string().matches(phoneRegExp, 'Invalid phone number').required('Please enter your phone number'),
  email: Yup.string().matches(emailRegExp, 'Invalid email address').required('Please enter your email'),
  postalCode: Yup.string().matches(postalRegExp, 'Invalid Postal Code').required('Please enter your postal code'),
});

const formFieldEloquaIDs = {
  email: 220,
  firstname: 221,
  lastname: 222,
  phone: 223,
  comments: 292,
  postalCode: 226,
};

const initialValues = {
  firstname: '',
  lastname: '',
  phone: '',
  email: '',
  postalCode: '',
  comments: ''
};

const formRequiredComplete = (form) => {
  const allComplete =
    form.values.firstname !== '' &&
    form.values.lastname !== '' &&
    form.values.email !== '' &&
    form.values.phone !== '' &&
    form.values.postalCode !== '';
  return allComplete;
};

export default function EmailForm(pageProps) {
  const [isSubmit, setIsSubmit] = useState(false);
  const { dealer, province, postalCode, locale, router, getSummaryUrl, cancel } = pageProps;

  const formik = useFormik({
    initialValues: {
      ...initialValues,
      postalCode: postalCode || '',
    },
    enableReinitialize: true,
    validationSchema: formSchema,
    validateOnChange: false,
    onSubmit: async (values) => {
      const url = await getSummaryUrl();
      const data = formDataForSubmit(values, url);
      setIsSubmit(true);

      API.post('/api/contactForm', data).then(
        (res) => {
          setIsSubmit(false);

          if (res['id']) {
            router.push(`/contact-success?dealer=${dealer?.dealerURLName?.toString()}`);
          } else if (res) {
            const errors = {};
            res.forEach((feedback) => {
              const eloquaID = feedback.property;
              if (eloquaID) {
                const formFieldID = eloquaIDtoFormFieldID(eloquaID);
                if (formFieldID) {
                  const eloquaMsg = feedback.requirement?.type;
                  errors[formFieldID] = 'Invalid: ' + eloquaMsg;
                }
              }
            });
            formik.setErrors(errors);
          }
        },
        (err) => {
          setIsSubmit(false);
          console.warn('Problem submitted form: ', err);
        }
      );
    }
  });

  const eloquaIDtoFormFieldID = (eloquaID) => {
    let fieldID = null;
    for (const fID in formFieldEloquaIDs) {
      if (formFieldEloquaIDs[fID] == eloquaID) {
        fieldID = fID;
      }
    }
    return fieldID;
  };

  const formDataForSubmit = (values, url) => {
    var dealerOverride = values?.firstname.toString() == 'NAT020'? 'NAT020' : dealer?.dealerCode;
    return {
      type: 'FormData',
      fieldValues: [
        {
          type: 'FieldValue',
          id: formFieldEloquaIDs.email,
          name: 'Email Address',
          value: values?.email.toString()
        },
        {
          type: 'FieldValue',
          name: 'First Name',
          id: formFieldEloquaIDs.firstname,
          value: values?.firstname.toString()
        },
        {
          type: 'FieldValue',
          name: 'Last Name',
          id: formFieldEloquaIDs.lastname,
          value: values?.lastname.toString()
        },
        {
          type: 'FieldValue',
          name: 'Business Phone',
          id: formFieldEloquaIDs.phone,
          value: values?.phone.toString()
        },
        {
          type: 'FieldValue',
          name: 'comments',
          id: formFieldEloquaIDs.comments,
          value: values?.comments.toString()
        },
        {
          type: 'FieldValue',
          name: 'Zip or Postal Code',
          id: formFieldEloquaIDs.postalCode,
          value: values?.postalCode?.toString()
        },
        {
          type: 'FieldValue',
          name: 'subject',
          id: '294',
          value: window.build.SBDisp.Desc()
        },
        {
          type: 'FieldValue',
          name: 'department',
          id: '293',
          value: 'sales'
        },
        {
          type: 'FieldValue',
          name: 'dealercode',
          id: '258',
          value: dealerOverride
        },
        {
          type: 'FormField',
          name: 'build_quote',
          id: '822',
          value: url
        },
        {
          type: 'FieldValue',
          name: 'leadSource',
          id: '262',
          value: 'BuildandQuote'
        },
        {
          type: 'FieldValue',
          name: 'CLIENT_ID',
          id: '731',
          value: '36'
        },
        {
          type: 'FieldValue',
          name: 'CLIENT_NM',
          id: '732',
          value: 'Kubota'
        },
        {
          type: 'FieldValue',
          name: 'PROCESS_NM',
          id: '733',
          value: 'Lead Routing'
        },
        {
          type: 'FieldValue',
          name: 'INSERT_DT',
          id: '734',
          value: '1999-12-31'
        },
        {
          type: 'FieldValue',
          name: 'State or Province',
          id: '227',
          value: province
        },
        {
          type: 'FieldValue',
          name: 'Email Opt-In',
          id: '228',
          value: values?.optIn?.toString()
        },
        {
          type: 'FieldValue',
          name: 'language',
          id: '236',
          value: locale
        },
        {
          type: 'FieldValue',
          name: 'Interested Model',
          id: '325',
          value: window.build.SBDisp.Desc()
        },
        {
          type: 'FieldValue',
          name: 'Product Leads',
          id: '305',
          value: window.build.SBDisp.Desc()
        },
        {
          type: 'FieldValue',
          name: 'Date Submitted',
          id: '241',
          value: new Date()
        },
      ]
    };
  };

  return (
    <Container className={styles.emailForm}>
      <h2>{locale == 'fr' ? 'Envoyer le devis par courriel' : 'Email Quote'}</h2>
      <p className={styles.intro}>
        {locale == 'fr' ? "Si vous aimeriez discuter en plus de détails votre Kubota personnalisé avec un concessionnaire, veuillez remplir le formulaire si-dessous." : "If you'd like to have a dealer contact you to discuss your customized Kubota, please fill out the form below."}
      </p>
      <form id={styles.emailForm} onSubmit={formik.handleSubmit}>
        <h3>{locale == 'fr' ? 'Informations de l’utilisateur' : 'User Information'}</h3>
        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <input
              placeholder={locale == 'fr' ? 'Prénom *' : 'First Name *'}
              type="text"
              onChange={formik.handleChange}
              id="firstname"
              name="firstname"
              value={formik.values.firstname}
            />
            {formik.errors?.firstname && <div className={styles.formError}>{formik.errors?.firstname}</div>}
          </div>
          <div className={styles.formGroup}>
            <input
              placeholder={locale == 'fr' ? 'Nom de famille *' : 'Last Name *'}
              type="text"
              onChange={formik.handleChange}
              id="lastname"
              name="lastname"
              value={formik.values.lastname}
            />
            {formik.errors?.lastname && <div className={styles.formError}>{formik.errors?.lastname}</div>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <input
              placeholder={locale == 'fr' ? 'Adresse courriel *' : 'Email *'}
              type="email"
              onChange={formik.handleChange}
              id="email"
              name="email"
              value={formik.values.email}
            />
            {formik.errors?.email && <div className={styles.formError}>{formik.errors?.email}</div>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <input
              placeholder={locale == 'fr' ? 'Numéro de téléphone *' : 'Phone *'}
              type="text"
              onChange={formik.handleChange}
              id="phone"
              name="phone"
              value={formik.values.phone}
            />
            {formik.errors?.phone && <div className={styles.formError}>{formik.errors?.phone}</div>}
          </div>
          <div className={styles.formGroup}>
            <input
              placeholder={locale == 'fr' ? 'Code postal *' : 'Postal Code *'}
              type="text"
              onChange={formik.handleChange}
              id="postalCode"
              name="postalCode"
              value={formik.values.postalCode}
            />
            {formik.errors?.postalCode && <div className={styles.formError}>{formik.errors?.postalCode}</div>}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.formGroup}>
            <textarea
              placeholder={locale == 'fr' ? 'Commentaires' : 'Comments'}
              type="text"
              onChange={formik.handleChange}
              id="comments"
              name="comments"
              value={formik.values.comments}
              rows="6"
            ></textarea>
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.optInRow}>
            <CheckboxInput
              title={locale == 'fr' ? 'Oui, je souhaite recevoir des informations commerciales de la part de Kubota Canada Ltd. et du concessionnaire nommé ci-dessus concernant les produits Kubota, y compris des promotions, des offres, des nouvelles et des documents relatifs à la satisfaction des clients. Kubota Canada Ltd. demande le consentement pour elle-même et pour le concessionnaire. Je comprends que je peux me désinscrire à tout moment.<br><br>Si vous avez des questions, vous pouvez contacter Kubota Canada Ltd. à l’adresse 1155 Kubota Drive, Pickering, ON L1X 0H4 ou à kcl_g.customerservice@kubota.com ou votre concessionnaire aux coordonnées fournies ci-dessus.' : 'Yes, I would like to receive commercial information from Kubota Canada Ltd. and the dealer named above about Kubota products, including promotions, offers, news, and customer satisfaction material. Kubota Canada Ltd. Is asking for consent for itself and the dealer. I understand that I can unsubscribe at any time.<br><br>If you have any questions, you can contact Kubota Canada Ltd. at 1155 Kubota Drive, Pickering, ON L1X 0H4  or kcl_g.customerservice@kubota.com or your dealer at the information provided above.'}
              onChange={formik.handleChange}
              name="optIn"
              value={formik.values.optIn}
              id={'optIn'}
              checked={formik.values.optIn}
            />
          </div>
        </div>

        <div className={`${styles.formRow} ${styles.submitRow}`}>
          {!formRequiredComplete(formik) && (
            <div className={styles.formError}>
              {locale == 'fr' ? "Vous n'avez pas rempli tous les champs obligatoires." : 'You have not filled all required fields.'}
            </div>
          )}
        </div>
      </form>

      <div className={styles.buttonsRow}>
        <Button type="submit" className={`${styles.sendBtn} ${isSubmit && styles.disabled}`} onClick={formik.handleSubmit}>
          {isSubmit && <div className={`loader ${styles.loader}`}></div>} {locale == 'fr' ? 'Soumettre au concessionnaire' : 'Submit To Dealer'}
        </Button>
      </div>

      <div className={styles.backRow}>
        <Button type="button" className={styles.backBtn} onClick={cancel}>
          {locale == 'fr' ? 'Retour à Personnaliser' : 'Back To Customize'}
        </Button>
      </div>
    </Container>
  );
}
